import { useEffect, useState } from 'react'

interface UseStaggeredAnimationOptions {
  /**
   * Delay before starting the animation sequence (in milliseconds)
   * @default 100
   */
  initialDelay?: number

  /**
   * Delay between each item's animation start (in milliseconds)
   * @default 80
   */
  staggerDelay?: number
}

/**
 * Custom hook for smooth staggered left-to-right animations
 * Following Emil Kowalski's animation best practices
 *
 * @param options Configuration options for the animation
 * @returns Object containing animation state and helper functions
 */
export function useStaggeredAnimation(
  options: UseStaggeredAnimationOptions = {},
) {
  const { initialDelay = 100, staggerDelay = 80 } = options
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, initialDelay)

    return () => clearTimeout(timer)
  }, [initialDelay])

  /**
   * Get animation classes for an item
   * @returns CSS classes string for the animation
   */
  const getAnimationClasses = () => {
    return [
      'transition-all duration-700 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]',
      isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[-24px] opacity-0',
    ].join(' ')
  }

  /**
   * Get inline styles for an item at a specific index
   * @param index The index of the item in the sequence
   * @returns React style object
   */
  const getAnimationStyles = (index: number) => ({
    transitionDelay: `${index * staggerDelay}ms`,
  })

  /**
   * Get combined animation props for an item
   * @param index The index of the item in the sequence
   * @returns Object with className and style properties
   */
  const getAnimationProps = (index: number) => ({
    className: getAnimationClasses(),
    style: getAnimationStyles(index),
  })

  return {
    isVisible,
    getAnimationClasses,
    getAnimationStyles,
    getAnimationProps,
  }
}
